<template>
  <div id="skin-analysis-result-view">
    <div class="skin-analysis-result-header">
      <div class="skin-analysis-result-header-bottom-circle"></div>
      <div class="skin-age-group">
        <div class="skin-age-title">
          {{ $t("skinAnalysis.skinAge.skinAge") }}
        </div>
        <div class="skin-age-score-row">
          <div class="skin-age-content">{{ result.skinAge }}</div>
          <div class="skin-age-unit">
            {{ $t("skinAnalysis.skinAge.years") }}
          </div>
        </div>
      </div>

      <div class="skin-analysis-message">{{ getSkinAnalysisMessage }}</div>
    </div>

    <div class="skin-analysis-radar-container">
      <SkinScoreRadarChart
        :your-data="computeYourData"
        :average-data="averageData"
        :previous-your-data="computePreviousYourData"
      />
    </div>

    <div class="skin-analysis-button-container">
      <button
        type="button"
        class="skin-analysis-button"
        @click="goToTutorial()"
      >
        {{ $t("skinAnalysis.scan") }}
      </button>
    </div>

    <div class="info-container skin-type-container">
      <div class="skin-type-title-container">
        <div class="skin-type-title-prefix">
          {{ $t("skinAnalysis.skinType.youAre") }}
        </div>
        <div class="skin-type-name">{{ computeSkinTypeName }}</div>
        <div class="skin-type-description">
          {{ computeSkinTypeDescription }}
        </div>
      </div>
    </div>

    <div
      v-if="
        accountId &&
        (recommendModeList.length !== 0 || recommendMachineList.length !== 0)
      "
    >
      <div class="skin-analysis-result-recommend-title-1">RECOMMEND</div>
      <div class="skin-analysis-result-recommend-title-2">
        {{ $t("skinAnalysis.recommend.title") }}
      </div>
    </div>

    <div
      v-if="accountId && recommendModeList.length !== 0"
      class="info-container recommend-mode-based-on-type-container"
    >
      <div class="info-container-title">
        {{ $t("skinAnalysis.recommend.modeTitle") }}
      </div>
      <div
        v-for="(recommendMode, index) in recommendModeList"
        :key="recommendMode"
      >
        <RecommendMode :recommend-mode="recommendMode"></RecommendMode>
        <hr
          v-if="index < recommendModeList.length - 1"
          class="horizontal-line"
        />
      </div>
    </div>
    <div
      v-if="accountId && recommendMachineList.length !== 0"
      class="info-container skin-analysis-recommend-machine-container"
    >
      <div class="info-container-title">
        {{ $t("skinAnalysis.recommend.productTitle") }}
      </div>
      <div class="recommend-machine-component-container">
        <div
          v-for="recommendMachine in recommendMachineList"
          :key="recommendMachine"
        >
          <RecommendMachine
            :recommend-machine="recommendMachine"
            :skin-type-id="skinTypeId"
          ></RecommendMachine>
        </div>
      </div>
    </div>
    <div class="feature-annotation-container">
      <div class="feature-annotation">
        {{ $t("skinAnalysis.recommend.annotation.note1") }}
      </div>
      <div class="feature-annotation">
        {{ $t("skinAnalysis.recommend.annotation.note2") }}
      </div>
      <div class="feature-annotation">
        {{ $t("skinAnalysis.recommend.annotation.note3") }}
      </div>
      <div class="feature-annotation">
        {{ $t("skinAnalysis.recommend.annotation.note4") }}
      </div>
    </div>
    <div class="skin-analysis-result-footer-spacer"></div>
  </div>
</template>

<script>
import SkinScoreRadarChart from "@/components/SkinScoreRadarChart.vue";
import RecommendMode from "@/components/RecommendMode.vue";
import RecommendMachine from "@/components/RecommendMachine.vue";
import skinAnalysisMessageAlgorithm from "@/algorithms/skinAnalysisMessageAlgorithm";
import fetchAPI from "@/utils/fetch";
import consoleController from "@/utils/logUtil";
import dateUtil from "@/utils/dateUtil";
import errorUtil from "@/utils/errorUtil";
import eventLogUtil from "../utils/eventLogUtil";
import constant from "../const";
import store from "@/utils/store";

export default {
  components: {
    SkinScoreRadarChart,
    RecommendMode,
    RecommendMachine,
  },
  props: {
    accountId: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * resultは以下をパラメータに持つオブジェクト
     * skinAge
     * skinHealth
     * timed
     * ageSpots
     * moisture
     * texture
     * wrinkles
     * firmness
     * darkCirclesV2
     * pore
     */
    result: {
      type: Object,
      required: true,
    },
    skinTypeId: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: "type0",
      required: false,
    },
    masterData: {
      type: Object,
      required: true,
    },
    allData: {
      type: Object,
      require: false,
      default: undefined,
    },
    isError: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      // 世代ごとの平均を格納する
      averageData: [],
      // アカウントに紐づくデータ
      userData: {},
      machineIdList: [""],
      skinAnalysisDataList: [],
      isResultError: false,
      // おすすめモードに表示する機器
      recommendMachineIdsForMode: [],
      // おすすめモード
      recommendModeList: [],
      // おすすめ商品
      recommendMachineList: [],
    };
  },
  computed: {
    computeYourData() {
      if (!this.result) {
        return [0, 0, 0, 0, 0, 0, 0];
      }
      return [
        this.result.firmness,
        this.result.wrinkles,
        this.result.darkCirclesV2,
        this.result.pore,
        this.result.texture,
        this.result.ageSpots,
        this.result.moisture,
      ];
    },
    getSkinAnalysisMessage() {
      // 肌分析のハゲホメメッセージをアルゴリズムで検出して返す
      return skinAnalysisMessageAlgorithm.getSkinAnalysisMessage(
        this.result,
        this.skinAnalysisDataList,
      );
    },
    // 前回の肌分析結果
    computePreviousYourData() {
      if (this.skinAnalysisDataList.length === 0) {
        return undefined;
      }
      const list = this.skinAnalysisDataList;
      // 実施日付の降順に並び替え
      list.sort((a, b) =>
        dateUtil.sortISOStringDescending(a.dateTime, b.dateTime),
      );
      const previousData = list[0];
      // console.log(`previousData: ${JSON.stringify(previousData, null, 4)}`);
      const previousScoreList = [
        previousData.firmness,
        previousData.wrinkles,
        previousData.darkCirclesV2,
        previousData.pore,
        previousData.texture,
        previousData.ageSpots,
        previousData.moisture,
      ];
      return previousScoreList;
    },
    // 肌タイプ表示
    computeSkinTypeName() {
      if (this.masterData.skinType.length === 0) {
        return "";
      }
      const skinTypeInfo = this.masterData.skinType.find(
        (data) => data.skinTypeId === this.skinTypeId,
      );
      if (!skinTypeInfo) {
        consoleController("error", `skinTypeId: ${this.skinTypeId} is invalid`);
        return "";
      }
      return skinTypeInfo.skinTypeName;
    },
    computeSkinTypeDescription() {
      const skinTypeInfo = this.masterData.skinType.find(
        (data) => data.skinTypeId === this.skinTypeId,
      );
      if (!skinTypeInfo) {
        consoleController("error", `skinTypeId: ${this.skinTypeId} is invalid`);
        return "";
      }
      return skinTypeInfo.skinTypeDescription;
    },
  },
  watch: {
    skinTypeId(newVal, oldVal) {
      // 古い値が存在しない（初期化時の設定）場合は、mountedの処理に任せるためスキップ
      if (!oldVal) {
        return;
      }

      // デバッグ等で実際に値が変わったときだけ再計算
      if (newVal !== oldVal) {
        this.calcRecommendMachineList();
        this.calcRecommendModeList();
      }
    },
  },
  created() {},
  mounted() {
    this.sendDataLayerVirtualPageview();
    this.registerViewedPageEvent();
    if (this.accountId && this.accountId !== constant.GUEST_ACCOUNT_ID) {
      // 世代平均の取得
      this.getSkinAnalysisAverage();
      // 肌分析結果の送信
      this.postSkinAnalysisData();
    }
    // アカウントに紐づく情報
    if (this.allData) {
      [this.userData] = this.allData.userDataList;
      const machineData = this.allData.machineDataList[0];
      if (machineData && machineData.machineIdList) {
        this.machineIdList = machineData.machineIdList;
      }

      this.skinAnalysisDataList = this.allData.skinAnalysisDataList;
    }
    this.calcRecommendMachineList();
    this.calcRecommendModeList();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      const { scrollDownWrapper } = this.$refs;
      if (scrollDownWrapper) {
        const windowHeight = window.innerHeight;
        const { scrollHeight } = document.documentElement;
        const scrollTop =
          document.documentElement.scrollTop || document.body.scrollTop;

        // 小数点誤差を考慮して、下端から5px手前で下端到達とみなす
        if (scrollTop + windowHeight >= scrollHeight - 5) {
          scrollDownWrapper.style.display = "none";
        } else {
          scrollDownWrapper.style.display = "block";
        }
      }
    },
    goToTutorial() {
      this.registerClickedSkinAnalysisEvent();
      this.$router.replace({
        name: "TutorialView",
        query: { accountId: this.accountId },
      });
    },
    async getSkinAnalysisAverage() {
      if (this.accountId && this.accountId !== constant.GUEST_ACCOUNT_ID) {
        const { fetch } = fetchAPI;
        const path = "/web/get-skin-analysis-average";
        const body = {
          accountId: this.accountId,
        };
        try {
          const res = await fetch.fetchAPI("POST", path, null, body);
          if (res !== undefined && res.status === 200) {
            const responseData = await res.json();
            consoleController(
              "log",
              `get SkinAnalysisAverage is succeed. responseData: ${JSON.stringify(
                responseData,
                null,
                4,
              )}`,
            );
            if (responseData.length !== 0) {
              this.averageData = [
                responseData.firmness,
                responseData.wrinkles,
                responseData.darkCirclesV2,
                responseData.pore,
                responseData.texture,
                responseData.ageSpots,
                responseData.moisture,
              ];
            }
          } else if (res !== undefined && res.status !== 200) {
            const errorInfo = await res.json();
            errorUtil.createErrorDialog(errorInfo);
            consoleController(
              "error",
              `${path} is failed. errorInfo: ${JSON.stringify(
                errorInfo,
                null,
                4,
              )}`,
            );
          } else {
            errorUtil.createErrorDialog(undefined);
            consoleController("error", `${path} is failed.}`);
          }
        } catch (e) {
          if (!this.isError && !this.isResultError) {
            this.isResultError = true;
            const errorInfo = {
              errorMessage: this.$t(constant.OFFLINE_ERROR_MSG),
              errorCode: "E201-405",
            };
            errorUtil.createErrorDialog(errorInfo);
            consoleController("error", `${path} is failed.`);
            this.goToTutorial();
          }
        }
      }
    },
    async postSkinAnalysisData() {
      if (!this.accountId || this.accountId === constant.GUEST_ACCOUNT_ID) {
        consoleController(
          "log",
          "not post SkinAnalysisData. accountId is undefined",
        );
        return;
      }
      const currentDateTime = dateUtil.getCurrentISO_JST();
      // web側からのpostはdataにaccountIdを含める;
      const skinAnalysisData = {
        accountId: this.accountId,
        sortKeyId: crypto.randomUUID(),
        dateTime: currentDateTime,
        skinAge: this.result.skinAge,
        texture: this.result.texture,
        firmness: this.result.firmness,
        wrinkles: this.result.wrinkles,
        pore: this.result.pore,
        ageSpots: this.result.ageSpots,
        darkCirclesV2: this.result.darkCirclesV2,
        moisture: this.result.moisture,
        skinTypeId: this.skinTypeId,
      };
      const myCareMachineId = sessionStorage.getItem("myCareMachineId");
      if (myCareMachineId) {
        skinAnalysisData.myCareMachineId = myCareMachineId;
      }
      const { fetch } = fetchAPI;
      const path = `/web/set-skin-analysis-data`;
      try {
        const res = await fetch.fetchAPI("POST", path, null, skinAnalysisData);
        if (res !== undefined && res.status === 200) {
          const responseData = await res.json();
          consoleController(
            "log",
            `post SkinAnalysisData is succeed. responseData: ${JSON.stringify(
              responseData,
              null,
              4,
            )}`,
          );
        } else if (res !== undefined && res.status !== 200) {
          const errorInfo = await res.json();
          errorUtil.createErrorDialog(errorInfo);
          consoleController(
            "error",
            `${path} is failed. errorInfo: ${JSON.stringify(
              errorInfo,
              null,
              4,
            )}`,
          );
        } else {
          errorUtil.createErrorDialog(undefined);
          consoleController("error", `${path} is failed.}`);
        }
      } catch (e) {
        if (!this.isError && !this.isResultError) {
          this.isResultError = true;
          const errorInfo = {
            errorMessage: this.$t(constant.OFFLINE_ERROR_MSG),
            errorCode: "E201-405",
          };
          errorUtil.createErrorDialog(errorInfo);
          consoleController("error", `${path} is failed.`);
          this.goToTutorial();
        }
      }
    },
    // 下位品番が上位品番のおすすめ順位（優先度）を参照するためのメソッド
    getRecommendPriority(machineId, targetList) {
      let item = targetList.find((t) => t.machineId === machineId);
      if (item) return item.priority;

      // 該当品番がない場合、上位品番の順位を参照する
      if (machineId === "EH-SR85") {
        item = targetList.find((t) => t.machineId === "EH-SR86");
        if (item) return item.priority;
        item = targetList.find((t) => t.machineId === "EH-SR90");
        if (item) return item.priority;
      } else if (machineId === "EH-SR86") {
        item = targetList.find((t) => t.machineId === "EH-SR90");
        if (item) return item.priority;
      }

      return 9999; // おすすめ対象外の場合は低い優先度にする
    },
    // あなたへのおすすめ商品
    calcRecommendMachineList() {
      // 必要なデータが揃っているか確認する
      if (
        !this.skinTypeId ||
        !this.accountId ||
        !this.userData ||
        !this.masterData ||
        !this.masterData.machine ||
        this.masterData.machine.length === 0 ||
        !this.masterData.skinAnalysisRecommendMachine ||
        this.masterData.skinAnalysisRecommendMachine.length === 0
      ) {
        return;
      }

      // ユーザの年齢カテゴリを特定
      let ageCategory;
      if (
        this.userData?.dateOfBirth &&
        this.userData.dateOfBirth !== "0" &&
        store.currentCountry.value === constant.DEFAULT_COUNTRY
      ) {
        const birthDayjs = dateUtil.convertYYYYMMToDayjs_JST(
          this.userData.dateOfBirth,
        );
        if (
          dateUtil.isLessThan30YearsPassed(
            birthDayjs,
            dateUtil.getCurrentDayjs_JST(),
          )
        ) {
          ageCategory = "less_than_30";
        } else {
          ageCategory = "30_or_more";
        }
      } else {
        ageCategory = "undefined";
      }
      // 年齢カテゴリごとのおすすめでフィルタリング
      let targetList = this.masterData.skinAnalysisRecommendMachine.filter(
        (item) => item.ageCategory === ageCategory,
      );
      // 肌タイプごとのおすすめでフィルタリング
      targetList = targetList.filter(
        (item) => item.skinTypeId === this.skinTypeId,
      );
      // おすすめ商品のうち、おすすめモードを表示するものの機器IDを抽出
      if (store.currentCountry.value === constant.DEFAULT_COUNTRY) {
        const registeredMachinesWithPriority = this.machineIdList
          .map((machineId) => ({
            machineId,
            priority: this.getRecommendPriority(machineId, targetList),
          }))
          .filter((item) => item.priority !== 9999);

        // おすすめ順位（priority）の昇順でソート（値が小さいほど高順位）
        registeredMachinesWithPriority.sort((a, b) => a.priority - b.priority);

        // ソート済みの配列から機器IDを抽出
        this.recommendMachineIdsForMode = registeredMachinesWithPriority.map(
          (item) => item.machineId,
        );
        this.complementRecommendMachineIdsForMode();
      } else {
        // 海外の場合、おすすめ商品1位のおすすめモード1とおすすめモード2
        this.recommendMachineIdsForMode = [targetList[0].machineId];
      }

      // 既に登録済みの機器はおすすめ商品から除外
      const exclusionList = this.createRecommendMachineExclusionList();
      targetList = targetList.filter(
        (item) => !exclusionList.includes(item.machineId),
      );
      // 親品番がおすすめにある場合は子品番を除外
      if (targetList.some((item) => item.machineId === "EH-SR90")) {
        targetList = targetList.filter(
          (item) => item.machineId !== "EH-SR85", // EH-SR86は除外しない
        );
      }
      if (targetList.some((item) => item.machineId === "EH-SR86")) {
        targetList = targetList.filter((item) => item.machineId !== "EH-SR85");
      }
      targetList.sort((a, b) => a.priority - b.priority);

      const recommendMachineList = [];
      for (const targetMaster of targetList) {
        // 機器マスタからおすすめする商品(機器)の情報を取得
        const machineData = this.masterData.machine.find(
          (item) => item.machineId === targetMaster.machineId,
        );
        if (machineData) {
          const recommendMachine = {
            machineName: machineData.machineName,
            machineImageId: machineData.machineImageId,
            machineUrl: machineData.machineUrl,
          };
          if (machineData.machineId === "EH-SL85") {
            recommendMachine.machineName = "PHOTO\nBRIGHTSHOT";
          } else if (machineData.machineId === "EH-SS85") {
            recommendMachine.machineName = "IONBOOST\nMULTI EX";
          }
          recommendMachineList.push(recommendMachine);
        } else {
          consoleController(
            "error",
            `machineData is not found. machineId: ${targetMaster.machineId}`,
          );
        }
      }
      this.recommendMachineList = recommendMachineList;
    },
    complementRecommendMachineIdsForMode() {
      // 品番が登録されている かつ モード表示のためのおすすめ機器が足りない場合
      if (
        this.recommendMachineIdsForMode.length < 2 &&
        this.machineIdList.length >= 1
      ) {
        // 以下の優先順でユーザーが保持している機器をおすすめに追加する
        const targetMachineIds = [
          "EH-SR90",
          "EH-SR86",
          "EH-SR85",
          "EH-SP86",
          "EH-SB50",
          "EH-SB30",
        ];

        for (const machineId of targetMachineIds) {
          if (
            this.machineIdList.includes(machineId) &&
            !this.recommendMachineIdsForMode.includes(machineId)
          ) {
            this.recommendMachineIdsForMode.push(machineId);
          }
          if (this.recommendMachineIdsForMode.length >= 2) {
            break; // 必要な数が揃ったらループを抜ける
          }
        }
      }
    },
    // タイプに基づくおすすめ表示
    calcRecommendModeList() {
      // 必要なデータが揃っているか確認する
      if (
        !this.accountId ||
        this.masterData.skinType.length === 0 ||
        this.masterData.machine.length === 0 ||
        this.masterData.careMode.length === 0 ||
        this.machineIdList.length === 0
      ) {
        return;
      }

      const skinTypeInfo = this.masterData.skinType.find(
        (item) => item.skinTypeId === this.skinTypeId,
      );
      if (!skinTypeInfo) {
        return;
      }
      const skinTypeRecommendModeList = skinTypeInfo.recommendModeList;
      skinTypeRecommendModeList.sort((a, b) => a.priority - b.priority);

      const recommendModeList = [];
      // 肌タイプによるおすすめモードを表示する機器のID
      for (const recommendMachineId of this.recommendMachineIdsForMode) {
        // ループ中の機器IDでおすすめモードを追加する前のおすすめ数
        const beforePushLength = recommendModeList.length;
        // 肌タイプによるおすすめモードを表示
        for (const skinTypeRecommendMode of skinTypeRecommendModeList) {
          // 肌タイプによるおすすめモードの詳細情報
          const recommendModeId = skinTypeRecommendMode.recommendMode;
          const careModeInfo = this.masterData.careMode.find(
            (item) => item.careModeId === recommendModeId,
          );
          // 既に同じケアモードがリストに存在するかチェック
          const isDuplicate = recommendModeList.some(
            (mode) => mode.careModeName === careModeInfo.careModeName,
          );

          // 肌タイプによるおすすめモードに対応する機器がおすすめ対象かどうかを取得
          if (
            // 対象品番か
            careModeInfo.supportedModel.includes(recommendMachineId) ||
            // 併せ使いの対象品番か
            (careModeInfo.combinedUseModel !== undefined &&
              careModeInfo.combinedUseModel.includes(recommendMachineId) &&
              careModeInfo.combinedUseModel.every((model) =>
                this.machineIdList.includes(model),
              ))
          ) {
            if (!isDuplicate) {
              const machineData = this.masterData.machine.find(
                (item) => item.machineId === recommendMachineId,
              );

              const recommendModeItem = {
                machineImageId: machineData.machineImageId,
                careModeName: careModeInfo.careModeName,
                color: careModeInfo.color,
                detail: careModeInfo.modeDescription,
                isSupported: true,
              };
              recommendModeList.push(recommendModeItem);
            }
          } else if (
            careModeInfo.careModeId === "CLEAN" &&
            recommendMachineId === "EH-SR85"
          ) {
            // 対象外表示をするためにリストに追加
            const machineData = this.masterData.machine.find(
              (item) => item.machineId === recommendMachineId,
            );
            const recommendModeItem = {
              machineImageId: machineData.machineImageId,
              careModeName: careModeInfo.careModeName,
              color: careModeInfo.color,
              detail: careModeInfo.modeDescription,
              isSupported: false,
            };
            recommendModeList.push(recommendModeItem);
          }
          // おすすめ対象の品番が一つの場合は一つの品番のおすすめモードだけで二つ埋める
          if (this.recommendMachineIdsForMode.length == 1) {
            if (recommendModeList.length >= 2) {
              break;
            }
          } else if (recommendModeList.length > beforePushLength) {
            // ループ中の品番でおすすめモードが追加されている場合は次の品番へ
            break;
          }
        }
        if (recommendModeList.length == 2) {
          break;
        }
      }
      this.recommendModeList = recommendModeList;
    },
    // おすすめ商品として表示しない品番のリストを返却する
    createRecommendMachineExclusionList() {
      const exclusionList = [...this.machineIdList];
      // 上位品番が存在する場合は下位品番を表示対象外にする
      if (exclusionList.includes("EH-SR90")) {
        exclusionList.push("EH-SR86");
        exclusionList.push("EH-SR85");
      }
      if (exclusionList.includes("EH-SR86")) {
        exclusionList.push("EH-SR85");
      }
      return exclusionList;
    },
    // ページ表示時のデータレイヤ変数追加
    sendDataLayerVirtualPageview() {
      if (process.env.VUE_APP_GTM_ID !== "") {
        const dataLayer = window.dataLayer || [];
        let value = {
          event: "virtualPageview",
          pagePath: "/face-skin-analysis-stg/#/skin-analysis/result",
          pageTitle: "画像付肌分析結果画面",
        };
        if (process.env.NODE_ENV === "production") {
          value = {
            event: "virtualPageview",
            pagePath: "/face-skin-analysis/#/skin-analysis/result",
            pageTitle: "画像付肌分析結果画面",
          };
        }
        dataLayer.push(value);
        consoleController(
          "log",
          `push DataLayer: ${JSON.stringify(value, null, 4)}`,
        );
      }
    },
    // ページ表示時のイベントログ登録
    registerViewedPageEvent() {
      if (
        this.accountId !== undefined &&
        this.accountId !== constant.GUEST_ACCOUNT_ID
      ) {
        const screenId =
          eventLogUtil.getScreenIdList().skinAnalysisBrowserResultWithImage;
        const eventData = {
          screenId,
          skinAnalysisMessage: this.getSkinAnalysisMessage,
          result: this.result,
          skinTypeId: this.skinTypeId,
        };
        eventLogUtil.registerEvent(
          this.accountId,
          eventLogUtil.getEventCodeList().viewed_page,
          eventData,
        );
      }
    },
    // 肌分析ボタンのリンククリック時のイベントログ登録
    registerClickedSkinAnalysisEvent() {
      const eventData = {
        screenId:
          eventLogUtil.getScreenIdList().skinAnalysisBrowserSkinAnalysisButton,
      };
      eventLogUtil.registerEvent(
        this.accountId,
        eventLogUtil.getEventCodeList().clicked_skin_analysis,
        eventData,
      );
    },
  },
};
</script>

<style scoped>
#skin-analysis-result-view {
  margin-top: 0px;
  text-align: center;
}

.skin-analysis-result-header {
  /* Layout Properties */
  position: relative;
  height: 115px;
  width: 100vw;
  margin-top: 0px;
  /* UI Properties */
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 0 24px 0 22px;
  box-sizing: border-box;
}
.skin-analysis-result-header-bottom-circle {
  /* ヘッダー下側の弧を描く部分 */
  position: absolute;
  top: -187px;
  left: 0;
  width: 100vw;
  height: 300px;
  background-color: #eee;
  border-radius: 0 0 2000px 2000px / 0 0 300px 300px;
  text-align: center;
}
.skin-age-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
  flex-shrink: 0;
  z-index: 1;
}

.skin-age-score-row {
  display: flex;
  align-items: baseline;
}

.skin-age-title {
  /* UI Properties */
  text-align: center;
  vertical-align: top;
  font: normal normal normal 12px/30px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.9;
}
.skin-age-content {
  /* UI Properties */
  font: normal normal 100 50px/30px Roboto;
  vertical-align: top;
  text-align: right;
  color: #000000;
  opacity: 0.8;
}
.skin-age-unit {
  /* UI Properties */
  text-align: left;
  vertical-align: top;
  font: normal normal normal 14px/30px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.8;
}
.skin-analysis-message {
  /* UI Properties */
  text-align: left;
  vertical-align: top;
  font: normal normal normal 16px/24px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.9;
}

.skin-analysis-radar-container {
  height: calc(100vw + 30px);
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.skin-analysis-button-container {
  width: 100vw;
  text-align: center;
  margin-top: -24px;
  margin-bottom: 24px;
}
.skin-analysis-button {
  /* UI Properties */
  width: 158px;
  height: 52px;
  font: normal normal normal 16px/24px Noto Sans JP;
  letter-spacing: 0px;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); /* 影を追加 */
  background: #000000 0% 0% no-repeat padding-box;
  border-radius: 26px;
  opacity: 0.9;
  color: #fff;
  text-decoration: none;
  text-align: center;
  z-index: 100;
}

.skin-analysis-button::before {
  content: "";
  display: inline-block;
  width: 22px;
  height: 22px;
  margin: -5px 6px 0 0;
  background: url("~@/assets/images/icon_camera_line.png") no-repeat;
  background-size: cover;
  vertical-align: middle;
}

.info-container {
  /* UI Properties */
  width: 100vw;
  height: auto;
}

.info-container-title {
  /* Layout Properties */
  margin-left: 20px;
  /* UI Properties */
  text-align: left;
  font: normal normal 500 16px/24px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 1;
}

/* 肌タイプ */
.skin-type-container {
  width: 100vw;
  height: auto;
  margin-bottom: 24px;
}
.skin-type-title-container {
  width: 100%;
}
.skin-type-title-prefix {
  /* Layout Properties */
  margin: 0 auto;

  /* UI Properties */
  text-align: center;
  font: normal normal normal 24px/30px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
}
.skin-type-name {
  /* Layout Properties */
  margin: 0 auto;

  /* UI Properties */
  text-align: center;
  font: normal normal bold 24px/30px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
}
.skin-type-description {
  /* Layout Properties */
  margin-top: 16px;
  margin-right: 22px;
  margin-left: 22px;
  white-space: pre-wrap;

  /* UI Properties */
  text-align: left;
  font: normal normal normal 16px/24px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.9;
}

.skin-analysis-result-recommend-title-1 {
  margin-top: 24px;
  text-align: center;
  font: normal normal 100 28px/30px Roboto;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.9;
}

.skin-analysis-result-recommend-title-2 {
  margin-top: 4px;
  text-align: center;
  font: normal normal normal 14px/20px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.9;
}
/* おすすめモード */
.recommend-mode-based-on-type-container {
  margin-top: 24px;
}

/* おすすめ商品 */
.skin-analysis-recommend-machine-container {
  margin-top: 32px;
}
.recommend-machine-component-container {
  margin-top: 6px;
  margin-left: 18px;
  margin-right: 18px;
  /* スクロールバーの領域を確保する */
  padding-bottom: 10px;
  /* 横スクロールを可能にする */
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
}

.recommend-machine-component-container > div {
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: auto;
  margin-right: 20px;
}

.skin-analysis-result-footer-spacer {
  height: 90px;
}

.horizontal-line {
  margin-left: 16px;
  margin-right: 16px;
  border: 1px solid #00000033;
  opacity: 1;
  padding: 0px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.feature-annotation-container {
  margin-top: 14px;
  margin-left: 18px;
}
.feature-annotation {
  margin: 0;
  text-align: left;
  font: normal normal normal 10px/15px Noto Sans JP;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.6;
}
</style>
